import {Component, inject, OnInit, computed, DestroyRef, signal, ViewChild, ElementRef, AfterViewChecked} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {CopilotPromptBarComponent} from '../../components/copilot-prompt-bar/copilot-prompt-bar.component';
import {FormControl} from '@angular/forms';
import {ChatMessage, ChatMessageAuthor, UserMessageResponse} from '../../copilot.model';
import {UserMessageComponent} from "../../components/user-message/user-message.component";
import {CopilotMessageComponent} from "../../components/copilot-message/copilot-message.component";
import {EmptyChatMessageComponent} from '../../components/empty-chat-message/empty-chat-message.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PolarisRoutePaths, RoutePaths} from '../../../../shared/types/common';
import {TwoStageBrandPickerComponent} from '../../components/two-stage-brand-picker/two-stage-brand-picker.component';
import {DashboardService} from '../../../../services/dashboard.service';
import {PolarisRuntimeFacadeService} from '../../../../services/polaris/polaris-runtime-facade.service';
import {mapPolarisMessagesToChatMessages} from '../../adapters/polaris-message-to-chat-message.mapper';
import {AuthorizationService} from '../../../../services/authorization.service';
import {Permissions} from '../../../../shared/permissions';
import {PolarisModelVersion} from '../../../../services/polaris/model';

@Component({
  selector: 'app-copilot-chat-screen',
  imports: [CopilotPromptBarComponent, UserMessageComponent, CopilotMessageComponent, EmptyChatMessageComponent, TwoStageBrandPickerComponent],
  templateUrl: './copilot-chat-screen.component.html',
  styleUrl: './copilot-chat-screen.component.scss'
})

export class CopilotChatScreenComponent implements OnInit, AfterViewChecked {
  private polarisRuntimeFacadeService = inject(PolarisRuntimeFacadeService);
  private authorizationService = inject(AuthorizationService);
  private dashboardService = inject(DashboardService);
  private route = inject(ActivatedRoute);
  private destroy$ = inject(DestroyRef);
  private router = inject(Router);
  public chatId = signal<string | null>(null);
  public canSelectV2 = this.authorizationService.hasPermissions([Permissions.SEND_POLARIS_V2_MESSAGE]);

  private chatVm = computed(() => {
    const id = this.chatId();
    if (!id) return undefined;
    return this.polarisRuntimeFacadeService.state.chats()[id];
  });

  public chatModelVersion = computed<PolarisModelVersion>(() => this.chatVm()?.modelVersion ?? 'v1');

  public myBrand = computed<string | undefined>(() => {
    const raw = this.chatVm()?.myBrand;
    if (raw == null) return undefined;
    const allowed = this.dashboardService.brandsOptions().myBrands?.some(brand => brand.key === raw);
    return allowed ? raw : undefined;
  });

  public myCompetitors = computed<string[]>(() => this.chatVm()?.myCompetitors ?? []);
  public responseOverrides = signal<Record<string, UserMessageResponse>>({});
  public userHasInteracted = signal(false);
  private lastAmountOfMessages = 0;
  private routeLoadVersion = 0;
  @ViewChild('scrollAnchor') scrollAnchor!: ElementRef;

  promptControl = new FormControl('');
  messages = computed<ChatMessage[]>(() => {
    const currentChatId = this.chatId();
    if (!currentChatId) return [];
    const overrides = this.responseOverrides();
    return mapPolarisMessagesToChatMessages(this.polarisRuntimeFacadeService.getChatMessages(currentChatId)())
      .map((message) => ({
        ...message,
        userResponse: overrides[message.id] ?? message.userResponse
      }));
  });

  showEmptyState = computed(() => {
    const vm = this.chatVm();
    return vm != null &&
      this.messages().length === 0 &&
      !vm.isNewlyCreated &&
      !this.userHasInteracted() &&
      !this.polarisRuntimeFacadeService.state.isLoadingActiveChat();
  });

  ngOnInit(): void {
    this.route.params.pipe(takeUntilDestroyed(this.destroy$)).subscribe(async params => {
      const newChatId = params['id'];
      const routeLoadVersion = ++this.routeLoadVersion;
      this.chatId.set(newChatId);
      this.userHasInteracted.set(false);
      this.polarisRuntimeFacadeService.setRecentChatId(newChatId);

      if (!newChatId) {
        this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.POLARIS_HOME}`]);
        return;
      }
      try {
        const isLoaded = await this.polarisRuntimeFacadeService.ensureChatLoaded(newChatId);
        if (this.isStaleRouteLoad(routeLoadVersion, newChatId)) {
          return;
        }
        if (!isLoaded) {
          this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.POLARIS_HOME}`]);
          return;
        }
      } catch {
        if (this.isStaleRouteLoad(routeLoadVersion, newChatId)) {
          return;
        }
        this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.POLARIS_HOME}`]);
      }
    });
  }

  ngAfterViewChecked() {
    if (this.lastAmountOfMessages !== this.messages().length)
      this.scrollToAnchor();
    this.lastAmountOfMessages = this.messages().length;
  }

  async onSendMessage(message: string): Promise<void> {
    const currentChatId = this.chatId();
    if (!currentChatId) return;
    this.userHasInteracted.set(true);
    const result = await this.polarisRuntimeFacadeService.sendMessage(currentChatId, message);
    if (result.status === 'chat_not_found') {
      await this.navigateToPolarisHome();
    }
  }

  async onRetryRequested(event: { chatId: string; interactionId?: string }): Promise<void> {
    const currentChatId = this.chatId();
    if (!currentChatId) {
      return;
    }
    if (!event.interactionId) {
      return;
    }
    const userPrompt = this.messages()
      .find((message) => message.author === ChatMessageAuthor.USER && message.interactionId === event.interactionId)
      ?.content;
    if (!userPrompt) {
      return;
    }
    const result = await this.polarisRuntimeFacadeService.sendMessage(currentChatId, userPrompt);
    if (result.status === 'chat_not_found') {
      await this.navigateToPolarisHome();
    }
  }

  onFollowUpChatNotFound(): void {
    void this.navigateToPolarisHome();
  }

  private async navigateToPolarisHome(): Promise<void> {
    await this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.POLARIS_HOME}`]);
  }

  onResponseSelected(event: { messageId: string; response: UserMessageResponse }): void {
    this.responseOverrides.update((overrides) => ({
      ...overrides,
      [event.messageId]: event.response
    }));
  }

  isLatestAssistantMessage(message: ChatMessage): boolean {
    const latestAssistantMessage = [...this.messages()]
      .reverse()
      .find((candidate) => candidate.author === ChatMessageAuthor.ASSISTANT);
    return latestAssistantMessage?.id === message.id;
  }

  private scrollToAnchor() {
    if (this.scrollAnchor) {
      this.scrollAnchor.nativeElement.scrollIntoView({behavior: 'smooth'});
    }
  }

  private isStaleRouteLoad(routeLoadVersion: number, expectedChatId: string): boolean {
    return routeLoadVersion !== this.routeLoadVersion || this.chatId() !== expectedChatId;
  }
}
